import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAuth } from '../../../context/AuthContext';
import {
  fetchCommissionsByPeriod,
  markCommissionsAsPaid,
} from '../services/commissionService';
import { fetchEmployeesByBusiness } from '../../employees/services/employeeService';
import useCommissionsStore from '../store/commissionsStore';

/**
 * Prim raporu için veri çekme ve mutasyon hook'u.
 *
 * Server state: records (commission_records), employees (join için)
 * UI state: commissionsStore'da yaşar
 */
export function useCommissions() {
  const { user }           = useAuth();
  const { t }              = useTranslation('settings');
  const businessId         = user?.businessId;

  const selectedPeriod  = useCommissionsStore((s) => s.selectedPeriod);
  const statusFilter    = useCommissionsStore((s) => s.statusFilter);
  const selectedRecordIds = useCommissionsStore((s) => s.selectedRecordIds);
  const clearSelection  = useCommissionsStore((s) => s.clearSelection);

  const [records,   setRecords]   = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [paying,    setPaying]    = useState(false);

  // ── Veri çekme ─────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    try {
      const [recs, emps] = await Promise.all([
        fetchCommissionsByPeriod(businessId, selectedPeriod, statusFilter),
        fetchEmployeesByBusiness(businessId),
      ]);
      setRecords(recs);
      setEmployees(emps);
    } catch (err) {
      console.warn('[useCommissions] load:', err.message);
    } finally {
      setLoading(false);
    }
  }, [businessId, selectedPeriod, statusFilter]);

  useEffect(() => { load(); }, [load]);

  // ── Çalışan bazlı gruplama ─────────────────────────────────
  const employeeMap = useMemo(
    () => Object.fromEntries(employees.map((e) => [e.id, e])),
    [employees]
  );

  const groupedByEmployee = useMemo(() => {
    const groups = {};
    records.forEach((rec) => {
      if (!groups[rec.employeeId]) {
        groups[rec.employeeId] = {
          employee: employeeMap[rec.employeeId] || { id: rec.employeeId, name: '?', surname: '' },
          records:          [],
          totalCommission:  0,
          pendingCount:     0,
          paidCount:        0,
        };
      }
      const g = groups[rec.employeeId];
      g.records.push(rec);
      g.totalCommission += rec.totalCommission || 0;
      if (rec.status === 'pending') g.pendingCount++;
      if (rec.status === 'paid')    g.paidCount++;
    });
    return Object.values(groups).sort((a, b) =>
      `${a.employee.name} ${a.employee.surname}`.localeCompare(
        `${b.employee.name} ${b.employee.surname}`, 'tr'
      )
    );
  }, [records, employeeMap]);

  // ── Ödendi işareti ─────────────────────────────────────────
  const markAsPaid = useCallback(async () => {
    if (!selectedRecordIds.length) return;
    setPaying(true);
    try {
      await markCommissionsAsPaid(selectedRecordIds, user.uid);
      toast.success(`${selectedRecordIds.length} kayıt ödendi olarak işaretlendi.`);
      clearSelection();
      await load();
    } catch (err) {
      console.error('[useCommissions] markAsPaid:', err);
      toast.error('İşlem sırasında hata oluştu.');
    } finally {
      setPaying(false);
    }
  }, [selectedRecordIds, user?.uid, clearSelection, load]);

  return { groupedByEmployee, records, loading, paying, markAsPaid };
}
